import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ImpersonationBanner from './components/ImpersonationBanner';
import BugReportButton from './components/BugReportButton';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SharedLinkPage from './pages/SharedLinkPage';
import AdminPage from './pages/AdminPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import FriendsPage from './pages/FriendsPage';
import DiscoverPage from './pages/DiscoverPage';
import ProfilePage from './pages/ProfilePage';
import ConlangLayout from './pages/ConlangLayout';
import OverviewPage from './pages/conlang/OverviewPage';
import PhonologyPage from './pages/conlang/PhonologyPage';
import PhonotacticsPage from './pages/conlang/PhonotacticsPage';
import AllophonesPage from './pages/conlang/AllophonesPage';
import ProsodyPage from './pages/conlang/ProsodyPage';
import WordOrderPage from './pages/conlang/WordOrderPage';
import PartsOfSpeechPage from './pages/conlang/PartsOfSpeechPage';
import GrammarPage from './pages/conlang/GrammarPage';
import MorphologyDescPage from './pages/conlang/MorphologyDescPage';
import DerivationPage from './pages/conlang/DerivationPage';
import InflectionPage from './pages/conlang/InflectionPage';
import LexiconCategoryPage from './pages/conlang/LexiconCategoryPage';
import LexiconPage from './pages/conlang/LexiconPage';
import MorphologyPage from './pages/conlang/MorphologyPage';
import SyntaxPage from './pages/conlang/SyntaxPage';
import TextsPage from './pages/conlang/TextsPage';
import WritingSystemsPage from './pages/conlang/WritingSystemsPage';
import WritingSystemDetailPage from './pages/conlang/WritingSystemDetailPage';
import SettingsPage from './pages/conlang/SettingsPage';
import ActivityLogPage from './pages/conlang/ActivityLogPage';
import { Loader2 } from 'lucide-react';
import './App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If still checking auth, show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  // If user data was passed via location state, allow access
  if (location.state?.user) {
    return children;
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Router component to handle OAuth callback detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id - must be synchronous
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/shared/:token" element={<SharedLinkPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/friends" 
        element={
          <ProtectedRoute>
            <FriendsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/discover" 
        element={
          <ProtectedRoute>
            <DiscoverPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Conlang Routes */}
      <Route 
        path="/conlang/:conlangId" 
        element={
          <ProtectedRoute>
            <ConlangLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        {/* Phonology */}
        <Route path="phonology" element={<PhonologyPage />} />
        <Route path="phonotactics" element={<PhonotacticsPage />} />
        <Route path="allophones" element={<AllophonesPage />} />
        <Route path="prosody" element={<ProsodyPage />} />
        {/* Syntax */}
        <Route path="word-order" element={<WordOrderPage />} />
        <Route path="parts-of-speech" element={<PartsOfSpeechPage />} />
        <Route path="grammar" element={<GrammarPage />} />
        {/* Morphology */}
        <Route path="morphology-description" element={<MorphologyDescPage />} />
        <Route path="derivation" element={<DerivationPage />} />
        <Route path="inflection" element={<InflectionPage />} />
        <Route path="inflection/:posFilter" element={<InflectionPage />} />
        {/* Lexicon */}
        <Route path="lexicon/pronouns" element={<LexiconCategoryPage category="pronoun" />} />
        <Route path="lexicon/numbers" element={<LexiconCategoryPage category="number" />} />
        <Route path="lexicon/kinship" element={<LexiconCategoryPage category="kinship" />} />
        <Route path="lexicon/colors" element={<LexiconCategoryPage category="color" />} />
        <Route path="lexicon/body-parts" element={<LexiconCategoryPage category="body_part" />} />
        <Route path="lexicon" element={<LexiconPage />} />
        {/* Legacy routes */}
        <Route path="morphology" element={<MorphologyPage />} />
        <Route path="syntax" element={<SyntaxPage />} />
        {/* Writing */}
        <Route path="writing" element={<WritingSystemsPage />} />
        <Route path="writing/:systemId" element={<WritingSystemDetailPage />} />
        {/* Other */}
        <Route path="texts" element={<TextsPage />} />
        <Route path="activity" element={<ActivityLogPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      
      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Show bug report button only for authenticated users
const AuthenticatedExtras = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <BugReportButton />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="App">
            <ImpersonationBanner />
            <AppRouter />
            <AuthenticatedExtras />
            <Toaster position="top-center" richColors />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
